// OWNER: FRONTEND-ADMIN. The voicemail-drop recorder card (SPEC §15.8). Shows the campaign's drop script with
// {setter}/{callback} filled as a suggestion the setter may rewrite in their own words (PJ, 2026-10-03), records up to
// 30 s, converts to 8 kHz mono 16-bit WAV in the browser, plays it back, and uploads it only after the setter attests
// it is their own human voice. Admins and Growth Specialists tick the review checklist themselves and are approved on
// upload; every other setter's recording waits for an admin.
import { useEffect, useRef, useState } from 'preact/hooks';
import type { Campaign } from '../../../src/shared/api';
import { errorMessage } from '../api';
import { ErrorBanner, toast } from '../components/ui';
import { useMe } from '../me';
import { adminApi, type RecordingReview } from './endpoints';
import { spokenPhone } from './phone';
import { RecorderError, openMic, pickMimeType, recorderUnsupportedReason, stopStream, toDropWav, type ProcessedRecording } from './capture';
import { MAX_DROP_SECONDS } from './wav';
import { Check, Field, inputValue } from './ui';
import { anyCampaignDropScripts, dropRecorderBlocker, fillDropScript, firstName, normalizeScript } from './validate';

type Phase = 'idle' | 'recording' | 'processing' | 'ready' | 'uploading';

/** The review checklist: what an admin confirms, and what a self-approving Growth Specialist ticks on upload. */
export const RECORDING_CHECKS: Array<{ key: keyof RecordingReview['checklist']; label: string }> = [
  { key: 'legalNameFirst', label: 'Says the setter’s name and “Valistix” in the first sentence' },
  { key: 'callbackNumber', label: 'States a callback number clearly (our main line is best: those calls ring the dialer and get logged)' },
  { key: 'under30s', label: `Under ${MAX_DROP_SECONDS} seconds` },
  { key: 'humanVoice', label: 'A real human voice, the setter’s own (not AI, cloned or synthetic)' },
  { key: 'noClaims', label: 'No claims: no results promises, stats, health or cannabis-effect claims' },
];
const NO_CHECKS: RecordingReview['checklist'] = { legalNameFirst: false, callbackNumber: false, under30s: false, humanVoice: false, noClaims: false };

export function Recorder(props: { campaigns: Campaign[]; callbackE164: string | null; onUploaded: () => void }) {
  const me = useMe();
  // every open campaign is listed; one without drops (every Canadian one) shows why instead of a recorder
  const open = props.campaigns.filter((c) => c.status !== 'done');
  // 'any' (PJ, 2026-10-03): a general voicemail saved for every campaign (campaign_id NULL). Always offered first and
  // chosen by default, even with no campaign that allows drops; its suggested wording comes from the first campaign
  // that allows drops, else the built-in general script (validate.ts GENERAL_DROP_SCRIPT).
  const [campaignId, setCampaignId] = useState<number | 'any'>('any');
  const anyCampaign = campaignId === 'any';
  const general = anyCampaignDropScripts(open);
  const selected = anyCampaign ? null : open.find((c) => c.id === campaignId) ?? null;
  const noDrops = selected ? dropRecorderBlocker(selected) : null;
  const campaign = selected && !noDrops ? selected : null;
  const scripts = anyCampaign ? general.scripts : campaign ? normalizeScript(campaign.script).voicemailsDrop : [];
  const [situation, setSituation] = useState<string>(scripts[0]?.situation ?? '');
  const script = scripts.find((s) => s.situation === situation) ?? scripts[0] ?? null;

  const [phase, setPhase] = useState<Phase>('idle');
  const [elapsed, setElapsed] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ProcessedRecording | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [attested, setAttested] = useState(false);
  const [policy, setPolicy] = useState<{ selfApprove: boolean } | null>(null);
  const [checks, setChecks] = useState<RecordingReview['checklist']>(NO_CHECKS);
  const [text, setText] = useState('');
  const stream = useRef<MediaStream | null>(null);
  const rec = useRef<MediaRecorder | null>(null);
  const timer = useRef<number | null>(null);
  const alive = useRef(true);

  const unsupported = recorderUnsupportedReason();
  const callback = props.callbackE164 ? spokenPhone(props.callbackE164) : null;
  const filled = script ? fillDropScript(script.text, { setter: firstName(me.name), callback }) : null;
  const selfApprove = policy?.selfApprove === true;
  const allChecked = Object.values(checks).every(Boolean);
  const blocker = !anyCampaign && !selected
    ? 'Pick what to record for.'
    : noDrops
      ? null // shown as its own notice below, instead of the recorder
    : !callback
      ? 'No main callback number is set yet, and every drop must say it. An admin sets it in Settings → Calling; recording opens once it is set.'
      : null;
  // The setter's own words: a recorded drop is reused on every call, so it can't hold a {placeholder}.
  const textProblem = !text.trim()
    ? 'Write what you will say (start from the suggestion or use your own words).'
    : /\{[a-z_]+\}/i.test(text) ? 'Replace every {placeholder} with real words: a recorded drop is reused on every call, so it can’t name the shop or the person.'
    : text.length > 2000 ? 'Keep the script under 2,000 characters.' : null;

  useEffect(() => {
    let live = true;
    adminApi.recordingPolicy().then((p) => { if (live) setPolicy(p); }).catch(() => { if (live) setPolicy({ selfApprove: false }); });
    return () => { live = false; };
  }, []);
  // start each script from the campaign's suggestion
  useEffect(() => { setText(filled ? filled.text : ''); }, [filled?.text]);

  const clearPreview = () => {
    setResult(null); setAttested(false); setChecks(NO_CHECKS);
    setPreviewUrl((u) => { if (u) URL.revokeObjectURL(u); return null; });
  };
  const reset = () => { clearPreview(); setPhase('idle'); };
  const stopTimer = () => { if (timer.current !== null) { window.clearInterval(timer.current); timer.current = null; } };

  useEffect(() => () => {
    alive.current = false;
    stopTimer();
    if (rec.current && rec.current.state !== 'inactive') { rec.current.onstop = null; rec.current.stop(); }
    stopStream(stream.current);
    setPreviewUrl((u) => { if (u) URL.revokeObjectURL(u); return null; });
  }, []);

  // keep the situation valid when the campaign changes
  useEffect(() => { if (!scripts.some((s) => s.situation === situation)) setSituation(scripts[0]?.situation ?? ''); }, [anyCampaign ? 'any' : campaign?.id]);

  const start = async () => {
    setError(null); clearPreview();
    try {
      const s = await openMic();
      if (!alive.current) { stopStream(s); return; }
      stream.current = s;
      const mime = pickMimeType();
      const r = new MediaRecorder(s, mime ? { mimeType: mime } : undefined);
      const chunks: Blob[] = [];
      r.ondataavailable = (e) => { if (e.data && e.data.size) chunks.push(e.data); };
      r.onerror = () => { setError('The recording failed in the browser. Try again.'); };
      r.onstop = async () => {
        stopTimer();
        stopStream(stream.current); stream.current = null;
        if (!alive.current) return;
        setPhase('processing');
        try {
          const out = await toDropWav(new Blob(chunks, { type: r.mimeType || mime || 'audio/webm' }));
          if (!alive.current) return;
          setResult(out);
          setPreviewUrl(URL.createObjectURL(out.wav));
          setPhase('ready');
        } catch (e) {
          setError(e instanceof RecorderError ? e.message : `Couldn't convert the recording: ${errorMessage(e)}`);
          setPhase('idle');
        }
      };
      rec.current = r;
      r.start(250);
      const t0 = performance.now();
      setElapsed(0);
      setPhase('recording');
      timer.current = window.setInterval(() => {
        const secs = (performance.now() - t0) / 1000;
        setElapsed(secs);
        if (secs >= MAX_DROP_SECONDS && r.state === 'recording') r.stop();
      }, 100);
    } catch (e) {
      stopStream(stream.current); stream.current = null;
      setError(e instanceof RecorderError ? e.message : `Couldn't start recording: ${errorMessage(e)}`);
      setPhase('idle');
    }
  };

  const stop = () => { if (rec.current && rec.current.state === 'recording') rec.current.stop(); };

  const upload = async () => {
    if (!result || !script || (!anyCampaign && !campaign)) return;
    if (textProblem) { setError(textProblem); return; }
    if (!attested) { setError('Tick the box to confirm this is your own voice.'); return; }
    if (selfApprove && !allChecked) { setError('Tick every checklist item: your recording is approved when you upload it.'); return; }
    setPhase('uploading'); setError(null);
    try {
      await adminApi.uploadRecording(result.wav, {
        situation: script.situation, campaignId: anyCampaign || !campaign ? null : campaign.id, scriptText: text.trim(),
        ...(selfApprove ? { checklist: checks } : {}),
      });
      toast(selfApprove ? 'Recording uploaded and approved.' : 'Recording uploaded. An admin reviews it before it can be used.', 'ok');
      clearPreview();
      setPhase('idle');
      props.onUploaded();
    } catch (e) {
      setError(errorMessage(e));
      setPhase('ready');
    }
  };

  return (
    <div class="card stack">
      <h3>Record a voicemail drop</h3>
      <div class="adm-sub">
        Record in your own voice. The script below is a suggestion: your own words are fine. Every recording must start with your name and
        “Valistix”, give a callback number, stay under {MAX_DROP_SECONDS} seconds and make no promises or stats. Never use AI or synthetic voices.
        {selfApprove ? ' As a Growth Specialist, you confirm the checklist yourself and the recording is approved when you upload it.' : ' An admin listens to it and approves it.'}
        {' '}Once approved, it can be dropped
        on US business landlines in every state except California, Washington, Texas, Tennessee, Oklahoma and Michigan (the dialer checks the line type when you press V);
        never cell phones, toll-free or any Canadian number.
      </div>
      <div class="adm-grid">
        <Field label="Campaign">
          <select value={anyCampaign ? 'any' : String(selected?.id ?? '')} disabled={phase !== 'idle' && phase !== 'ready'}
            onChange={(e) => { const v = inputValue(e); setCampaignId(v === 'any' ? 'any' : Number(v)); reset(); }}>
            <option value="any">Any campaign (general voicemail)</option>
            {open.map((c) => <option value={String(c.id)}>{c.name}{dropRecorderBlocker(c) ? ' (live voicemails only)' : ''}</option>)}
          </select>
        </Field>
        {anyCampaign || campaign ? (
          <Field label="Script">
            <select value={script?.situation ?? ''} disabled={phase !== 'idle' && phase !== 'ready'} onChange={(e) => { setSituation(inputValue(e)); reset(); }}>
              {scripts.map((s) => <option value={s.situation}>{s.label} ({s.situation})</option>)}
            </select>
          </Field>
        ) : null}
      </div>
      {noDrops ? (
        <div class="adm-muted-box stack" role="status">
          <b style="font-size:16px;color:var(--ink)">{noDrops}</b>
          <span>
            {selected?.country === 'CA'
              ? 'Pre-recorded voicemails are never left on Canadian numbers. When you reach voicemail, read the live voicemail script from the dialer (press L on the call).'
              : 'Read the live voicemail script from the dialer when you reach voicemail (press L on the call). An admin can add drop scripts in Campaigns → Script → Drop scripts.'}
          </span>
        </div>
      ) : null}
      {!noDrops && unsupported ? <div class="chip chip-bad" style="display:block;padding:8px 10px">{unsupported}</div> : null}
      {blocker ? <div class="chip chip-warn" style="display:block;padding:8px 10px">{blocker}</div> : null}
      {filled && !blocker ? (
        <Field label="Your script" hint="Suggested wording. Change it to your own words; write what you actually say so the reviewer can follow along.">
          <textarea rows={4} value={text} disabled={phase === 'recording' || phase === 'uploading'} onInput={(e) => setText(inputValue(e))} />
        </Field>
      ) : null}
      {filled && !blocker && textProblem && text.trim() ? <div class="chip chip-warn" style="display:block;padding:8px 10px">{textProblem}</div> : null}
      {anyCampaign && !blocker ? (
        <div class="adm-sub">
          Plays on any of your campaigns that allow drops. Keep it general (no one campaign's offer); {general.from
            ? <>the suggestion above comes from {general.from.name}.</>
            : 'the suggestion above is the general one.'}
        </div>
      ) : null}

      {noDrops || (!anyCampaign && !selected) ? null : <div class="row" style="flex-wrap:wrap">
        {phase === 'recording' ? (
          <>
            <button class="btn btn-danger" onClick={stop}>Stop</button>
            <span class="adm-rec-dot" aria-hidden="true" />
            <b class="mono">{elapsed.toFixed(1)} s</b><span class="adm-sub">/ {MAX_DROP_SECONDS} s (stops automatically)</span>
          </>
        ) : (
          <button class="btn btn-ink" disabled={!!unsupported || !!blocker || phase === 'processing' || phase === 'uploading'} onClick={() => void start()}>
            {phase === 'ready' ? 'Record again' : 'Start recording'}
          </button>
        )}
        {phase === 'processing' ? <span class="adm-sub">Converting to phone-quality audio…</span> : null}
      </div>}
      <ErrorBanner error={error} />

      {result && previewUrl && (phase === 'ready' || phase === 'uploading') ? (
        <div class="stack">
          <audio controls src={previewUrl} style="width:100%" />
          <div class="adm-sub">{result.durationSec.toFixed(1)} s · 8 kHz mono WAV · {(result.bytes / 1024).toFixed(0)} KB. Listen back: legal name first, callback number clear, no claims.</div>
          <Check checked={attested} onChange={setAttested} label={<b>I recorded this myself, in my own voice. It is not AI-generated, cloned or synthetic.</b>} />
          {selfApprove ? (
            <div class="stack" style="gap:4px">
              <span class="adm-sub">Growth Specialist: listen back and confirm each item. Your recording is approved when you upload it.</span>
              {RECORDING_CHECKS.map((c) => <Check checked={checks[c.key]} onChange={(v) => setChecks({ ...checks, [c.key]: v })} label={c.label} />)}
            </div>
          ) : null}
          <div class="row">
            <button class="btn btn-primary" disabled={!attested || (selfApprove && !allChecked) || !!textProblem || phase === 'uploading'} onClick={() => void upload()}>{phase === 'uploading' ? 'Uploading…' : selfApprove ? 'Upload and approve' : 'Upload for review'}</button>
            <button class="btn" disabled={phase === 'uploading'} onClick={reset}>Discard</button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
